import { useMemo } from 'react';
import { Box, Paper, Typography, Stack, Chip, useTheme } from '@mui/material';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import dayjs from 'dayjs';
import type { SymptomReading } from '@lorrainemarwood-care/shared';

type Series = 'breathlessness' | 'pain' | 'sleep' | 'appetite' | 'mood' | 'appearance';

const SERIES: { key: Series; label: string }[] = [
  { key: 'breathlessness', label: 'Breath' },
  { key: 'pain', label: 'Pain' },
  { key: 'sleep', label: 'Sleep' },
  { key: 'appetite', label: 'Appetite' },
  { key: 'mood', label: 'Mood' },
  { key: 'appearance', label: 'Look' },
];

export default function SymptomChart({ readings }: { readings: SymptomReading[] }) {
  const theme = useTheme();
  const colors: Record<Series, string> = {
    breathlessness: theme.palette.error.main,
    pain: theme.palette.warning.main,
    sleep: theme.palette.info.main,
    appetite: theme.palette.success.main,
    mood: theme.palette.primary.main,
    appearance: theme.palette.secondary.main,
  };

  const data = useMemo(() => {
    return [...readings]
      .sort((a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime())
      .map((r) => ({
        t: new Date(r.occurredAt).getTime(),
        label: dayjs(r.occurredAt).format('DD MMM HH:mm'),
        breathlessness: r.breathlessness,
        pain: r.pain,
        sleep: r.sleep,
        appetite: r.appetite,
        mood: r.mood,
        appearance: r.appearance,
      }));
  }, [readings]);

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Symptom trends</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {SERIES.map((s) => (
            <Chip
              key={s.key}
              size="small"
              label={s.label}
              variant="outlined"
              sx={{ borderColor: colors[s.key], color: colors[s.key] }}
            />
          ))}
        </Stack>
      </Stack>
      <Box sx={{ width: '100%', height: 320 }}>
        {data.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
            No readings yet.
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 12, right: 16, left: -20, bottom: 12 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 10]}
                ticks={[0, 2, 4, 6, 8, 10]}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {SERIES.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.label}
                  stroke={colors[s.key]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
}
